import Cookies from 'js-cookie';
import { parseCookie } from 'cookie';

const AUTH_CODE = [401, 420];
const expires = 180;
const redirectKey = 'atom_redirect';
const userKey = 'atom_user';
const fileHost = import.meta.env.VITE_FILE_HOST;

const ALLOWED_AUTH_REDIRECT_DOMAIN_SUFFIXES = [
  'atomgit.com',
  'atomgit.net',
  'openatom.cn',
  'openatom.org',
  'openatom.tech',
  'devedu.net',
  'openharmony.cn',
  'hwcloudtest.cn',
  'oafdev.cn',
  'ossvd.cn',
] as const;

const ALLOWED_AUTH_REDIRECT_HOSTNAMES = [
  '192.168.10.20',
  'localhost',
  '127.0.0.1',
] as const;

const getCookieDomain = () => {
  if (import.meta.env.VITE_FORCE_TRANSFER_LOGIN === 'yes' || import.meta.env.DEV) {
    return typeof location === 'undefined' ? undefined : location.hostname;
  }

  return import.meta.env.VITE_COOKIE_DOMAIN;
};

const isDomainOrSubdomain = (hostname: string, domain: string) => {
  return hostname === domain || hostname.endsWith(`.${domain}`);
};

const normalizePhoto = (value: unknown) => {
  const raw = String(value ?? '').trim();

  if (!raw) {
    return '';
  }

  if (raw.startsWith(fileHost)) {
    const path = raw.slice(fileHost.length);

    return path.startsWith('/') ? path : `/${path}`;
  }

  if (/^https?:\/\//.test(raw)) {
    return raw;
  }

  return raw.startsWith('/') ? raw : `/${raw}`;
};

export const isLogin = (cookieStr?: string) => {
  if (import.meta.env.VITE_MOCK_LOGIN === 'yes') {
    return true;
  }
  else {
    const source = cookieStr ?? (typeof document === 'undefined' ? '' : document.cookie);
    const cookieObject = parseCookie(source);

    return Boolean(cookieObject[userKey]);
  }
};

export const getUser = (cookieStr?: string) => {
  const source = cookieStr ?? (typeof document === 'undefined' ? '' : document.cookie);
  const cookieObject = parseCookie(source);

  try {
    return JSON.parse(cookieObject[userKey] || '{}');
  }
  catch (err) {
    return {};
  }
};

export const setUser = (info: Record<string, any>) => {
  const nextUser = {
    ...(info || {}),
    photo: normalizePhoto(info?.photo),
  };

  Cookies.set(userKey, JSON.stringify(nextUser), {
    path: '/',
    expires,
    domain: getCookieDomain(),
  });
};

export const clearUser = () => {
  Cookies.remove(userKey, {
    path: '/',
    domain: getCookieDomain(),
  });
};

export const gotoLogin = async (from = '/') => {
  setRedirectUrl(from);

  if (import.meta.env.VITE_FORCE_TRANSFER_LOGIN === 'yes' || import.meta.env.DEV) {
    // 开发环境走中转逻辑，以生产换件为跳板

    const base = `${window.location.protocol}//${window.location.host}`;
    const redirect = `${base}/callback?from=${encodeURIComponent(from)}`;
    const loginPath = `${import.meta.env.VITE_LOGIN_CALLBACK_URL}?transfer_redirect=${encodeURIComponent(redirect)}`;

    window.location.replace(loginPath);
  }
  else {
    // 生产和测试环境走正式逻辑

    window.location.replace(import.meta.env.VITE_AUTH_BASE_URL);
  }
};

export const gotoLogout = (from: string) => {
  clearUser();

  window.location.replace(`${import.meta.env.VITE_LOGOUT_URL}?redirect_uri=${encodeURIComponent(from)}`);
};

export const parseHttpUrl = (url: string, base?: string) => {
  try {
    const parsedUrl = base ? new URL(url, base) : new URL(url);

    if (parsedUrl.protocol !== 'http:' && parsedUrl.protocol !== 'https:') {
      return null;
    }

    return parsedUrl;
  }
  catch {
    return null;
  }
};

export const isAllowedAuthRedirectHostname = (hostname: string) => {
  return ALLOWED_AUTH_REDIRECT_HOSTNAMES.includes(
    hostname as typeof ALLOWED_AUTH_REDIRECT_HOSTNAMES[number],
  ) || ALLOWED_AUTH_REDIRECT_DOMAIN_SUFFIXES.some(domain => (
    isDomainOrSubdomain(hostname, domain)
  ));
};

export const parseAllowedAuthRedirectUrl = (url: string, base?: string) => {
  const parsedUrl = parseHttpUrl(url, base);

  if (!parsedUrl || !isAllowedAuthRedirectHostname(parsedUrl.hostname)) {
    return null;
  }

  return parsedUrl;
};

export const isAuthCode = (code: number) => AUTH_CODE.includes(code);

export function getRedirectUrl() {
  const url = Cookies.get(redirectKey) || '';

  Cookies.remove(redirectKey, {
    path: '/',
    domain: getCookieDomain(),
  });

  return url;
}

export function setRedirectUrl(url: string) {
  if (url) {
    Cookies.set(redirectKey, url, {
      path: '/',
      expires,
      domain: getCookieDomain(),
    });
  }
}
